/*
  ============================================================
  Pickup Locator – PLZ-Suche & Distanzberechnung
  ============================================================
  Liest Store-Daten aus der pickup-availability Component und
  erweitert sie um:
  - PLZ-Eingabe (mit Nominatim Geocoding, OpenStreetMap)
  - Browser-Geolocation
  - Distanzsortierung (Haversine)
  - Persistenz der zuletzt genutzten PLZ via localStorage
  
  Trigger: <pickup-availability> Custom Element vom Theme
  Datenquelle: data-pl-store Attribute pro Store-Item
*/

(function() {
  'use strict';

  const STORAGE_KEY = 'pl:lastZip';
  const STORAGE_COORDS = 'pl:lastCoords';
  
  // Haversine-Formel: Distanz zwischen zwei Geokoordinaten in km
  function haversineKm(lat1, lon1, lat2, lon2) {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) ** 2 +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  }

  // PLZ → Koordinaten via Nominatim (OpenStreetMap)
  async function geocodeZip(zip, country) {
    country = country || 'Germany';
    const url = `https://nominatim.openstreetmap.org/search?postalcode=${encodeURIComponent(zip)}&country=${encodeURIComponent(country)}&format=json&limit=1`;
    try {
      const res = await fetch(url, {
        headers: { 'Accept-Language': 'de' }
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (!data.length) return null;
      return {
        lat: parseFloat(data[0].lat),
        lon: parseFloat(data[0].lon),
        display: data[0].display_name
      };
    } catch (err) {
      console.error('Geocoding failed', err);
      return null;
    }
  }

  // Distanz formatieren
  function fmtDistance(km) {
    if (km < 1) return Math.round(km * 1000) + ' m';
    if (km < 10) return km.toFixed(1).replace('.', ',') + ' km';
    return Math.round(km) + ' km';
  }

  // Eine Pickup-Komponente initialisieren
  function initPickup(root) {
    if (root.dataset.plInit === '1') return;
    root.dataset.plInit = '1';

    const drawerId = root.querySelector('[aria-controls]')?.getAttribute('aria-controls');
    const drawer = drawerId ? document.getElementById(drawerId) : null;
    if (!drawer) return;

    const locator = drawer.querySelector('[data-pl-locator]');
    const list = drawer.querySelector('[data-pl-list]');
    if (!locator || !list) return;

    const input = locator.querySelector('[data-pl-input]');
    const submitBtn = locator.querySelector('[data-pl-submit]');
    const geoBtn = locator.querySelector('[data-pl-geo]');
    const status = locator.querySelector('[data-pl-status]');
    const clearBtn = locator.querySelector('[data-pl-clear]');

    // Stores aus DOM lesen
    const items = Array.from(list.querySelectorAll('[data-pl-store]'));
    const stores = items.map(el => ({
      el: el,
      lat: parseFloat(el.dataset.plLat),
      lon: parseFloat(el.dataset.plLon),
      available: el.dataset.plAvailable === 'true'
    })).filter(s => !isNaN(s.lat) && !isNaN(s.lon));

    // Status setzen
    function setStatus(msg, type) {
      if (!status) return;
      status.textContent = msg || '';
      status.dataset.type = type || '';
    }

    // Liste neu sortieren basierend auf Position
    function sortByPosition(lat, lon, label) {
      stores.forEach(s => {
        s.distance = haversineKm(lat, lon, s.lat, s.lon);
        const badge = s.el.querySelector('[data-pl-distance]');
        if (badge) {
          badge.textContent = fmtDistance(s.distance);
          badge.hidden = false;
        }
      });
      // Sort: erst verfügbare, dann nicht-verfügbare; jeweils nach Distanz
      const sorted = [...stores].sort((a,b) => {
        if (a.available !== b.available) return a.available ? -1 : 1;
        return a.distance - b.distance;
      });
      // DOM neu anordnen
      sorted.forEach(s => list.appendChild(s.el));
      
      // Direktanzeige (closest-location) updaten
      updateClosestDisplay(sorted);

      if (label) {
        setStatus('Sortiert nach Entfernung von ' + label, 'success');
      }
      if (clearBtn) clearBtn.hidden = false;
    }

    // Außerhalb vom Drawer: die "closest location" Anzeige updaten
    function updateClosestDisplay(sortedStores) {
      const closestWrap = root.querySelector('[data-pl-closest]');
      if (!closestWrap || !sortedStores.length) return;
      
      const closest = sortedStores.find(s => s.available) || sortedStores[0];
      const nameEl = closestWrap.querySelector('[data-pl-closest-name]');
      const distEl = closestWrap.querySelector('[data-pl-closest-distance]');
      
      if (nameEl && closest.el.dataset.plName) {
        nameEl.textContent = closest.el.dataset.plName;
      }
      if (distEl) {
        distEl.textContent = fmtDistance(closest.distance);
        distEl.hidden = false;
      }
    }

    // PLZ-Suche durchführen
    async function searchZip(zip) {
      zip = (zip || '').trim();
      if (!/^\d{4,5}$/.test(zip)) {
        setStatus('Bitte eine gültige Postleitzahl eingeben.', 'error');
        return;
      }
      setStatus('Suche…', 'loading');
      const coords = await geocodeZip(zip);
      if (!coords) {
        setStatus('Postleitzahl nicht gefunden.', 'error');
        return;
      }
      sortByPosition(coords.lat, coords.lon, zip);
      try {
        localStorage.setItem(STORAGE_KEY, zip);
        localStorage.setItem(STORAGE_COORDS, JSON.stringify(coords));
      } catch(e) {}
    }

    // Geolocation nutzen
    function useGeolocation() {
      if (!navigator.geolocation) {
        setStatus('Standortermittlung nicht unterstützt.', 'error');
        return;
      }
      setStatus('Standort wird ermittelt…', 'loading');
      navigator.geolocation.getCurrentPosition(
        pos => {
          sortByPosition(pos.coords.latitude, pos.coords.longitude, 'deinem Standort');
          if (input) input.value = '';
          try {
            localStorage.setItem(STORAGE_COORDS, JSON.stringify({lat: pos.coords.latitude, lon: pos.coords.longitude}));
            localStorage.removeItem(STORAGE_KEY);
          } catch(e) {}
        },
        err => {
          if (err.code === 1) setStatus('Standortzugriff wurde abgelehnt.', 'error');
          else setStatus('Standort konnte nicht ermittelt werden.', 'error');
        },
        { timeout: 8000, maximumAge: 60000 }
      );
    }

    // Filter zurücksetzen
    function clearFilter() {
      stores.forEach(s => {
        delete s.distance;
        const badge = s.el.querySelector('[data-pl-distance]');
        if (badge) badge.hidden = true;
      });
      // Original-Reihenfolge wiederherstellen
      items.forEach(el => list.appendChild(el));
      setStatus('', '');
      if (clearBtn) clearBtn.hidden = true;
      if (input) input.value = '';
      try {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(STORAGE_COORDS);
      } catch(e) {}
      // Direktanzeige zurücksetzen
      const closestWrap = root.querySelector('[data-pl-closest]');
      const distEl = closestWrap?.querySelector('[data-pl-closest-distance]');
      if (distEl) distEl.hidden = true;
    }

    // Event Listeners
    if (submitBtn) {
      submitBtn.addEventListener('click', e => {
        e.preventDefault();
        searchZip(input?.value);
      });
    }
    if (input) {
      input.addEventListener('keydown', e => {
        if (e.key === 'Enter') {
          e.preventDefault();
          searchZip(input.value);
        }
      });
    }
    if (geoBtn) {
      geoBtn.addEventListener('click', e => {
        e.preventDefault();
        useGeolocation();
      });
    }
    if (clearBtn) {
      clearBtn.addEventListener('click', e => {
        e.preventDefault();
        clearFilter();
      });
      clearBtn.hidden = true;
    }

    // Beim Init: gespeicherte PLZ wieder anwenden
    try {
      const savedCoords = localStorage.getItem(STORAGE_COORDS);
      const savedZip = localStorage.getItem(STORAGE_KEY);
      if (savedCoords) {
        const c = JSON.parse(savedCoords);
        if (c && typeof c.lat === 'number') {
          sortByPosition(c.lat, c.lon, savedZip || 'deinem letzten Standort');
          if (input && savedZip) input.value = savedZip;
        }
      }
    } catch(e) {}
  }

  // Auf alle pickup-availability Komponenten anwenden
  function init() {
    document.querySelectorAll('pickup-availability').forEach(initPickup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Bei Variant-Änderungen erneut initialisieren (Theme rendert pickup neu)
  document.addEventListener('variant:change', init);
  // MutationObserver als Fallback, falls Theme den Block austauscht
  const obs = new MutationObserver(muts => {
    for (const m of muts) {
      for (const node of m.addedNodes) {
        if (node.nodeType === 1 && node.matches?.('pickup-availability')) {
          initPickup(node);
        } else if (node.nodeType === 1) {
          node.querySelectorAll?.('pickup-availability').forEach(initPickup);
        }
      }
    }
  });
  obs.observe(document.body, { childList: true, subtree: true });
})();
